import React, { useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import moment from 'moment-timezone';

import { PageHeader } from '../../../../components/PageHeader';
import { Button, HEADER_BUTTON_DARK_BLUE } from '../../../../components/PageHeader/Button';
import Table3, { columnHelper } from '../../../../containers/Table/Table3';
import SimpleModal from '../../../../containers/Modal/SimpleModal';
import Select from '../../../../components/Select';
import { TextInput } from '../../../../components/Inputs/TextInput';
import { notificationActions } from '../../../../components/Notification/redux/actions';
import { openModalAction } from '../../../../actions/modal';
import NewConfirmationModal from '../../../../modals/NewConfirmationModal/NewConfirmationModal';
import { GET_MY_ORGS_RESULT } from '../../../../components/OrgSelect/orgFilterActions';
import Strings from '../../../../Strings';
import { DATE_MONTH_DAY_YEAR, DATE_TIME_SPACE_12_UPPERCASE } from '../../../../constants';
import { getStoredOrgUUID, storeOrgUUID } from '../useAccessOrgSelection';
import accessStyles from '../AccessSimulator/AccessSimulator.module.scss';
import {
  actions,
  GET_ATHENA_SIM_COMM_LOG_RESULT,
  GET_ATHENA_SIM_CONFIG_RESULT,
  LIST_ATHENA_SIM_PATIENTS_RESULT,
  LIST_ATHENA_SIM_PROVIDERS_RESULT,
} from './actions';
import ProviderModal, { emptyProviderDraft, providerDraftFrom, providerDraftIsValid, providerFromDraft } from './ProviderModal';
import PatientModal, { emptyPatientDraft, patientDraftFrom, patientDraftIsValid, patientFromDraft } from './PatientModal';

// athenaOne's preview practice, used until the org has a practice configured.
const DEFAULT_PRACTICE_ID = 195900;

const FAILURE_OPTIONS = [
  { value: null, label: Strings.athenaSimAnswerNormally },
  ...[400, 401, 403, 404, 429, 500, 502, 503].map(status => ({ value: status, label: `HTTP ${status}` })),
];

const bodyPreview = body => {
  const oneLine = (body || '').replace(/\s+/g, ' ').trim();
  if (!oneLine) return '-';
  return oneLine.length > 300 ? `${oneLine.slice(0, 300)}…` : oneLine;
};

const fullName = row => `${row.last_name}, ${row.first_name}`;

/**
 * Superuser tab driving the athenaOne server simulator: pointing the org's
 * Athena credentials at it, the simulated practitioners and patients the
 * simulator serves, failure injection and the request log.
 */
function AthenaSimulator({
  userId, getMyOrgs, getConfig, listProviders, listPatients, getCommLog, pointOrg, unpointOrg, setSettings,
  addSampleData, reset, saveProvider, deleteProvider, savePatient, deletePatient, showNotification, openConfirmModal,
}) {
  const [orgs, setOrgs] = useState([]);
  const [orgsLoaded, setOrgsLoaded] = useState(false);
  const [orgUUID, setOrgUUID] = useState(null);
  const [config, setConfig] = useState(null);
  const [providers, setProviders] = useState([]);
  const [patients, setPatients] = useState([]);
  const [commLog, setCommLog] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [samplePracticeId, setSamplePracticeId] = useState('');
  const [providerModal, setProviderModal] = useState(null);
  const [patientModal, setPatientModal] = useState(null);

  const showError = resp => showNotification(
    resp?.response?.data?.standard_error?.message || Strings.errors.internalError,
    5000,
    true,
  );

  // loadGeneration bumps on every load so a slow response from an org the
  // user already switched away from is discarded instead of repopulating the
  // tab with the previous organization's data.
  const loadGeneration = useRef(0);

  const load = useCallback(targetOrgUUID => {
    const generation = loadGeneration.current + 1;
    loadGeneration.current = generation;
    setIsLoading(true);
    return Promise.all([
      getConfig(targetOrgUUID), listProviders(targetOrgUUID), listPatients(targetOrgUUID), getCommLog(targetOrgUUID),
    ]).then(([configResp, providersResp, patientsResp, logResp]) => {
      if (generation !== loadGeneration.current) return;
      if (configResp.type === GET_ATHENA_SIM_CONFIG_RESULT) {
        setConfig(configResp.response);
      } else {
        setConfig(null);
        showError(configResp);
      }
      setProviders(providersResp.type === LIST_ATHENA_SIM_PROVIDERS_RESULT ? providersResp.response.providers : []);
      setPatients(patientsResp.type === LIST_ATHENA_SIM_PATIENTS_RESULT ? patientsResp.response.patients : []);
      setCommLog(logResp.type === GET_ATHENA_SIM_COMM_LOG_RESULT ? logResp.response.entries : []);
      setIsLoading(false);
    });
  }, [getConfig, listProviders, listPatients, getCommLog]);

  useEffect(() => {
    getMyOrgs().then(resp => {
      if (resp.type === GET_MY_ORGS_RESULT) {
        const data = (resp.response?.data || []).map(org => ({ value: org.masked_id, label: org.name }));
        setOrgs(data);
        // The org picked on the ACCESS simulator tab is shared: both simulators
        // normally serve the same test organization.
        const stored = getStoredOrgUUID(userId);
        if (stored && data.some(org => org.value === stored)) setOrgUUID(stored);
      } else {
        showError(resp);
      }
    }).finally(() => setOrgsLoaded(true));
  }, [getMyOrgs, userId]);

  // Nothing to show until an organization is picked (a superuser usually has
  // none of their own).
  useEffect(() => {
    if (!orgsLoaded) return;
    if (orgUUID) load(orgUUID);
    else setIsLoading(false);
  }, [orgsLoaded, orgUUID, load]);

  const selectOrg = option => {
    const value = option?.value ?? null;
    storeOrgUUID(userId, value);
    // Drop the previous organization's data right away: the reload below is
    // async, and keeping the old rows visible would invite acting on them.
    setConfig(null);
    setProviders([]);
    setPatients([]);
    setCommLog([]);
    setOrgUUID(value);
  };

  // run performs a mutating call, reports the outcome and reloads the tab.
  // It resolves to whether the call succeeded. Mutations are off while a
  // load is in flight too: the tables may still show the previous org's rows.
  const run = (call, successMessage) => {
    if (isSaving || isLoading) return Promise.resolve(false);
    setIsSaving(true);
    return call().then(resp => {
      const ok = resp.type.endsWith('/result');
      if (!ok) showError(resp);
      else if (successMessage) showNotification(successMessage, 5000);
      return load(orgUUID).then(() => ok);
    }).finally(() => setIsSaving(false));
  };

  const practices = config?.practices || [];
  const defaultPracticeId = practices[0]?.practice_id || DEFAULT_PRACTICE_ID;
  const settings = config?.settings || {};

  const changeSetting = (field, value) => run(() => setSettings({ ...settings, [field]: value }, orgUUID));

  const saveProviderModal = () => run(
    () => saveProvider(providerModal.provider?.id, providerFromDraft(providerModal.draft), orgUUID),
  ).then(ok => ok && setProviderModal(null));

  const savePatientModal = () => run(
    () => savePatient(patientModal.patient?.id, patientFromDraft(patientModal.draft), orgUUID),
  ).then(ok => ok && setPatientModal(null));

  const confirmDelete = (text, call) => openConfirmModal({ text, onConfirm: () => run(call) });

  const providerColumns = [
    columnHelper.accessor('kind', {
      header: Strings.athenaSimKind,
      size: 110,
      cell: info => (info.getValue() === 'referring' ? Strings.athenaSimKindReferring : Strings.athenaSimKindProvider),
    }),
    columnHelper.accessor('practice_id', { header: Strings.athenaSimPractice, size: 100 }),
    columnHelper.accessor('provider_id', { header: Strings.athenaSimAthenaId, size: 90 }),
    columnHelper.accessor('last_name', { header: Strings.name, size: 190, cell: info => fullName(info.row.original) }),
    columnHelper.accessor('npi', { header: Strings.athenaSimNpi, size: 120, cell: info => info.getValue() || '-' }),
    columnHelper.accessor('specialty', { header: Strings.athenaSimSpecialty, size: 170, cell: info => info.getValue() || '-' }),
    columnHelper.accessor('fax', { header: Strings.athenaSimFax, size: 140, cell: info => info.getValue() || '-' }),
    columnHelper.accessor('direct_address', { header: Strings.athenaSimDirectAddress, size: 260, cell: info => info.getValue() || '-' }),
  ];

  const patientColumns = [
    columnHelper.accessor('practice_id', { header: Strings.athenaSimPractice, size: 100 }),
    columnHelper.accessor('athena_patient_id', { header: Strings.athenaSimAthenaId, size: 90 }),
    columnHelper.accessor('last_name', { header: Strings.name, size: 190, cell: info => fullName(info.row.original) }),
    columnHelper.accessor('dob', {
      header: Strings.athenaSimDob,
      size: 120,
      cell: info => moment(info.getValue()).format(DATE_MONTH_DAY_YEAR),
    }),
    columnHelper.accessor('mbi', { header: Strings.athenaSimMbi, size: 140, cell: info => info.getValue() || '-' }),
    columnHelper.accessor('primary_provider_id', { header: Strings.athenaSimPcp, size: 90, cell: info => info.getValue() || '-' }),
    columnHelper.accessor('problems', {
      header: Strings.athenaSimProblems,
      size: 280,
      enableSorting: false,
      cell: info => info.getValue().map(problem => (problem.status === 'resolved' ? `(${problem.icd10_code})` : problem.icd10_code)).join(', ') || '-',
    }),
  ];

  const logColumns = [
    columnHelper.accessor('created_at', {
      header: Strings.athenaSimTime,
      size: 190,
      cell: info => moment(info.getValue()).format(DATE_TIME_SPACE_12_UPPERCASE),
    }),
    columnHelper.accessor('path', {
      header: Strings.athenaSimRequest,
      size: 420,
      enableSorting: false,
      cell: info => `${info.row.original.method} ${info.getValue()}${info.row.original.query ? `?${info.row.original.query}` : ''}`,
    }),
    columnHelper.accessor('http_status', { header: Strings.athenaSimHttpStatus, size: 90 }),
    columnHelper.accessor('response_body', {
      header: Strings.athenaSimResponseBody, size: 520, enableSorting: false, cell: info => bodyPreview(info.getValue()),
    }),
  ];

  const noOrg = !config;

  return (
    <>
      <PageHeader
        left={Strings.athenaSimulatorTab}
        right={(
          <>
            <Button
              class={HEADER_BUTTON_DARK_BLUE}
              title={Strings.athenaSimReset}
              onClick={() => confirmDelete(Strings.athenaSimResetConfirm, () => reset(orgUUID))}
              disabled={isSaving || noOrg}
              testid="reset-athena-simulator"
            />
            {config?.pointed_at_simulator ? (
              <Button
                class={HEADER_BUTTON_DARK_BLUE}
                title={Strings.athenaSimUnpointOrg}
                onClick={() => confirmDelete(Strings.athenaSimUnpointOrgConfirm, () => unpointOrg(orgUUID))}
                disabled={isSaving}
                testid="unpoint-athena-simulator"
              />
            ) : (
              <Button
                class={HEADER_BUTTON_DARK_BLUE}
                title={Strings.athenaSimPointOrg}
                onClick={() => run(() => pointOrg(orgUUID))}
                disabled={isSaving || noOrg}
                testid="point-athena-simulator"
              />
            )}
          </>
        )}
      />

      <div className={accessStyles.accessSimulator}>
        <div className={accessStyles.accessSimulatorOrg}>
          <span className={accessStyles.accessSimulatorFieldLabel}>{Strings.accessSimOrg}</span>
          <Select id="athena-simulator-org" data={orgs} value={orgUUID} onChange={selectOrg} isSearchable disabled={isSaving || isLoading} />
        </div>
        <h2>{config?.pointed_at_simulator ? Strings.athenaSimPointed : Strings.athenaSimNotPointed}</h2>
        <div className={accessStyles.accessSimulatorStatus}>
          <div className={accessStyles.accessSimulatorField}>
            {Strings.athenaSimAthenaBaseUrl}
            <span className={accessStyles.accessSimulatorValue}>{config?.base_url || Strings.accessSimNotConfigured}</span>
          </div>
          <div className={accessStyles.accessSimulatorField}>
            <span data-tooltip-id="tooltip" data-tooltip-content={Strings.athenaSimPracticesTooltip}>{Strings.athenaSimPractices}</span>
            <span className={accessStyles.accessSimulatorValue}>
              {practices.map(practice => `${practice.practice_id}${practice.display_name ? ` (${practice.display_name})` : ''}${practice.enabled ? '' : ` - ${Strings.disabled}`}`).join(', ') || '-'}
            </span>
          </div>
          <div className={accessStyles.accessSimulatorField}>
            {Strings.athenaSimTokenFailure}
            <Select
              id="athena-simulator-token-failure"
              data={FAILURE_OPTIONS}
              value={settings.token_http_status ?? null}
              onChange={option => changeSetting('token_http_status', option?.value ?? null)}
              disabled={isSaving || noOrg}
            />
          </div>
          <div className={accessStyles.accessSimulatorField}>
            {Strings.athenaSimApiFailure}
            <Select
              id="athena-simulator-api-failure"
              data={FAILURE_OPTIONS}
              value={settings.api_http_status ?? null}
              onChange={option => changeSetting('api_http_status', option?.value ?? null)}
              disabled={isSaving || noOrg}
            />
          </div>
        </div>
        <p className={accessStyles.accessSimulatorNote}>
          {`${Strings.athenaSimNote} ${config?.simulator_url || ''}`}
        </p>
      </div>

      <div className={accessStyles.accessSimulatorTable}>
        <div className={accessStyles.accessSimulatorSectionHeader}>
          <h2>{Strings.athenaSimProviders}</h2>
          <div className={accessStyles.accessSimulatorSectionActions}>
            <TextInput
              id="athena-simulator-sample-practice"
              type="number"
              placeholder={`${Strings.athenaSimPractice} ${defaultPracticeId}`}
              value={samplePracticeId}
              onChange={event => setSamplePracticeId(event.target.value)}
              disabled={isSaving || noOrg}
            />
            <Button
              class={HEADER_BUTTON_DARK_BLUE}
              title={Strings.athenaSimAddSampleData}
              tooltip={Strings.athenaSimAddSampleDataTooltip}
              onClick={() => run(() => addSampleData(Number(samplePracticeId) || defaultPracticeId, orgUUID), Strings.athenaSimSampleDataAdded)}
              disabled={isSaving || noOrg}
              testid="athena-sim-sample-data"
            />
            <Button
              class={HEADER_BUTTON_DARK_BLUE}
              title={Strings.athenaSimAddProvider}
              onClick={() => setProviderModal({ provider: null, draft: emptyProviderDraft(defaultPracticeId) })}
              disabled={isSaving || noOrg}
              testid="add-athena-sim-provider"
            />
          </div>
        </div>
        <Table3
          name="athena-simulator-providers"
          data={providers}
          columns={providerColumns}
          isLoading={isLoading}
          noPagination
          buttons={[
            { icon: 'edit', onClick: (rowId, provider) => setProviderModal({ provider, draft: providerDraftFrom(provider) }), testId: 'edit-athena-sim-provider' },
            { icon: 'delete', onClick: (rowId, provider) => confirmDelete(Strings.athenaSimDeleteConfirm, () => deleteProvider(provider.id, orgUUID)), testId: 'delete-athena-sim-provider' },
          ]}
        />
      </div>

      <div className={accessStyles.accessSimulatorTable}>
        <div className={accessStyles.accessSimulatorSectionHeader}>
          <h2>{Strings.athenaSimPatients}</h2>
          <div className={accessStyles.accessSimulatorSectionActions}>
            <Button
              class={HEADER_BUTTON_DARK_BLUE}
              title={Strings.athenaSimAddPatient}
              onClick={() => setPatientModal({ patient: null, draft: emptyPatientDraft(defaultPracticeId) })}
              disabled={isSaving || noOrg}
              testid="add-athena-sim-patient"
            />
          </div>
        </div>
        <Table3
          name="athena-simulator-patients"
          data={patients}
          columns={patientColumns}
          isLoading={isLoading}
          noPagination
          buttons={[
            { icon: 'edit', onClick: (rowId, patient) => setPatientModal({ patient, draft: patientDraftFrom(patient) }), testId: 'edit-athena-sim-patient' },
            { icon: 'delete', onClick: (rowId, patient) => confirmDelete(Strings.athenaSimDeleteConfirm, () => deletePatient(patient.id, orgUUID)), testId: 'delete-athena-sim-patient' },
          ]}
        />
      </div>

      <div className={accessStyles.accessSimulatorTable}>
        <div className={accessStyles.accessSimulatorSectionHeader}>
          <h2>{Strings.athenaSimRequestLog}</h2>
          <div className={accessStyles.accessSimulatorSectionActions}>
            <Button
              class={HEADER_BUTTON_DARK_BLUE}
              title={Strings.athenaSimRefresh}
              onClick={() => load(orgUUID)}
              disabled={isLoading}
              testid="refresh-athena-sim-log"
            />
          </div>
        </div>
        <Table3 name="athena-simulator-log" data={commLog} columns={logColumns} isLoading={isLoading} />
      </div>

      {providerModal && (
        <SimpleModal
          name="athena-simulator-provider"
          title={providerModal.provider ? Strings.athenaSimEditProvider : Strings.athenaSimAddProvider}
          onCancel={() => setProviderModal(null)}
          onSubmit={saveProviderModal}
          isSubmitDisabled={isSaving || !providerDraftIsValid(providerModal.draft)}
          content={(
            <ProviderModal
              draft={providerModal.draft}
              setDraft={draft => setProviderModal({ ...providerModal, draft })}
              isSaving={isSaving}
            />
          )}
        />
      )}

      {patientModal && (
        <SimpleModal
          name="athena-simulator-patient"
          title={patientModal.patient ? Strings.athenaSimEditPatient : Strings.athenaSimAddPatient}
          onCancel={() => setPatientModal(null)}
          onSubmit={savePatientModal}
          isSubmitDisabled={isSaving || !patientDraftIsValid(patientModal.draft)}
          content={(
            <PatientModal
              draft={patientModal.draft}
              setDraft={draft => setPatientModal({ ...patientModal, draft })}
              providers={providers}
              isSaving={isSaving}
            />
          )}
        />
      )}
    </>
  );
}

AthenaSimulator.propTypes = {
  userId: PropTypes.oneOfType([PropTypes.number, PropTypes.oneOf([undefined, null])]),
  getMyOrgs: PropTypes.func.isRequired,
  getConfig: PropTypes.func.isRequired,
  listProviders: PropTypes.func.isRequired,
  listPatients: PropTypes.func.isRequired,
  getCommLog: PropTypes.func.isRequired,
  pointOrg: PropTypes.func.isRequired,
  unpointOrg: PropTypes.func.isRequired,
  setSettings: PropTypes.func.isRequired,
  addSampleData: PropTypes.func.isRequired,
  reset: PropTypes.func.isRequired,
  saveProvider: PropTypes.func.isRequired,
  deleteProvider: PropTypes.func.isRequired,
  savePatient: PropTypes.func.isRequired,
  deletePatient: PropTypes.func.isRequired,
  showNotification: PropTypes.func.isRequired,
  openConfirmModal: PropTypes.func.isRequired,
};

const mapDispatchToProps = dispatch => ({
  getMyOrgs: () => dispatch(actions.getMyOrgs()),
  getConfig: orgUUID => dispatch(actions.getConfig(orgUUID)),
  listProviders: orgUUID => dispatch(actions.listProviders(orgUUID)),
  listPatients: orgUUID => dispatch(actions.listPatients(orgUUID)),
  getCommLog: orgUUID => dispatch(actions.getCommLog(orgUUID)),
  pointOrg: orgUUID => dispatch(actions.pointOrg(orgUUID)),
  unpointOrg: orgUUID => dispatch(actions.unpointOrg(orgUUID)),
  setSettings: (settings, orgUUID) => dispatch(actions.setSettings(settings, orgUUID)),
  addSampleData: (practiceId, orgUUID) => dispatch(actions.addSampleData(practiceId, orgUUID)),
  reset: orgUUID => dispatch(actions.reset(orgUUID)),
  saveProvider: (id, provider, orgUUID) => dispatch(actions.saveProvider(id, provider, orgUUID)),
  deleteProvider: (id, orgUUID) => dispatch(actions.deleteProvider(id, orgUUID)),
  savePatient: (id, patient, orgUUID) => dispatch(actions.savePatient(id, patient, orgUUID)),
  deletePatient: (id, orgUUID) => dispatch(actions.deletePatient(id, orgUUID)),
  showNotification: (message, timeout, error) => dispatch(notificationActions.show(message, timeout, error)),
  openConfirmModal: data => dispatch(openModalAction(NewConfirmationModal.MODAL_NAME, data)),
});

const mapStateToProps = state => ({
  userId: state.auth?.profile?.id,
});

export default connect(mapStateToProps, mapDispatchToProps)(AthenaSimulator);
